import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useCookieConsent } from "../../lib/useCookieConsent";
import { ConsentActions } from "./ConsentActions";

// Under md every page shows the Design Studio's compact strip: one row (text · Accept · Decline), full width, sitting flush
// on top of the sticky Call Now / Free Visit bar (3.5rem + the iPhone home-indicator inset). md–lg keeps the card above
// that bar; lg+ (no bar) sits in the corner as before. The studio variant is compact at every width.
const MOBILE = "max-md:inset-x-0 max-md:bottom-[calc(3.5rem+env(safe-area-inset-bottom))] max-md:rounded-b-none max-md:border-x-0 max-md:border-b-0 max-md:px-4 max-md:py-3";
const POSITION = {
  studio: "md:bottom-[calc(4rem+env(safe-area-inset-bottom))] lg:bottom-4 md:right-4 md:left-auto md:max-w-xs md:p-4",
  site: "md:bottom-[calc(4rem+env(safe-area-inset-bottom))] lg:bottom-4 md:left-4 md:right-auto md:max-w-sm md:p-5",
};

export const CookieConsent = () => {
  const { pathname } = useLocation();
  // On /design the banner waits for the first scroll and stays compact (bottom-right on desktop), clear of the type cards.
  const studio = pathname === "/design";
  const { visible, decide } = useCookieConsent(studio);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className={`fixed z-[60] ${MOBILE} ${studio ? POSITION.studio : POSITION.site} bg-[#161616]/[0.98] backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl`}
          role="dialog"
          aria-label="Cookie consent"
          data-testid="cookie-consent"
          data-compact={studio ? "true" : undefined}
        >
          <div className="flex items-center gap-3 md:block">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="h-9 w-9 rounded-xl bg-[#FF9000]/15 place-items-center shrink-0 hidden md:grid">
                <Cookie className="text-[#FF9000]" size={18} />
              </div>
              <div className="min-w-0">
                <h3 className="text-white font-bold text-sm uppercase tracking-wider hidden md:block">Cookies</h3>
                <p className="text-white/60 text-xs leading-snug md:leading-relaxed md:mt-1.5">
                  <span className={studio ? "" : "md:hidden"}>Analytics cookies help us improve this {studio ? "tool" : "site"}. </span>
                  {!studio && <span className="hidden md:inline">We use analytics cookies to understand how visitors use our site. You can accept or decline. </span>}
                  See our <Link to="/privacy" className="text-[#FF9000] underline underline-offset-2 hover:text-white" data-testid="cookie-privacy-link">Privacy Policy</Link>.
                </p>
              </div>
            </div>
            <ConsentActions onDecide={decide} />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
